import { useEffect, useState } from "react";
import { Trophy, Clock } from "lucide-react";
import { Button } from "../ui/button";

export function GameQuiz({ match, currentUserId, onAnswer, onLeave }) {
    const state = match.state;
    const me = match.players.find((p) => p.userId === String(currentUserId));
    const opp = match.players.find((p) => p.userId !== String(currentUserId));
    const myScore = state.scores?.[me.userId] || 0;
    const oppScore = state.scores?.[opp.userId] || 0;
    const currentQ = state.questions?.[state.currentIndex];
    const [picked, setPicked] = useState(null);
    const [questionStart, setQuestionStart] = useState(Date.now());

    // Reset pick + start timer when question advances
    useEffect(() => {
        setPicked(null);
        setQuestionStart(Date.now());
    }, [state.currentIndex]);

    const reveal = currentQ && typeof currentQ.correctIndex === "number";

    const handlePick = (i) => {
        if (picked !== null || reveal || state.winner) return;
        setPicked(i);
        const timeMs = Date.now() - questionStart;
        onAnswer({ questionIndex: state.currentIndex, answerIndex: i, timeMs });
    };

    return (
        <div className="flex flex-col items-center">
            {/* Header */}
            <div className="flex items-center justify-between w-full max-w-lg gap-3 mb-3">
                <div className="flex-1">
                    <div className="text-xs text-gray-600 truncate">{me.userName} (you)</div>
                    <div className="text-2xl font-bold text-purple-700 tabular-nums">{myScore}</div>
                </div>
                <div className="text-center">
                    <div className="text-[10px] text-gray-400 font-semibold">QUESTION</div>
                    <div className="text-lg font-bold text-gray-900">{state.currentIndex + 1}/{state.totalQuestions}</div>
                </div>
                <div className="flex-1 text-right">
                    <div className="text-xs text-gray-600 truncate">{opp.userName}</div>
                    <div className="text-2xl font-bold text-gray-700 tabular-nums">{oppScore}</div>
                </div>
            </div>

            {!state.winner && currentQ && (
                <>
                    <div className="bg-white border border-gray-200 rounded-xl p-4 w-full max-w-lg shadow-sm">
                        <p className="text-base font-semibold text-gray-900 mb-3 leading-snug">{currentQ.question}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {currentQ.options.map((opt, i) => {
                                const isMyPick = picked === i;
                                const isCorrect = reveal && i === currentQ.correctIndex;
                                const isMyWrong = reveal && isMyPick && i !== currentQ.correctIndex;
                                return (
                                    <button
                                        key={i}
                                        onClick={() => handlePick(i)}
                                        disabled={picked !== null || reveal}
                                        className={`text-left px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                                            isCorrect ? "border-emerald-500 bg-emerald-50 text-emerald-800" :
                                            isMyWrong ? "border-red-400 bg-red-50 text-red-700" :
                                            isMyPick ? "border-purple-500 bg-purple-50 text-purple-800" :
                                            picked !== null || reveal ? "border-gray-200 bg-gray-50 text-gray-400" :
                                            "border-gray-200 bg-white text-gray-700 hover:border-purple-400 hover:bg-purple-50 cursor-pointer"
                                        }`}
                                    >
                                        <span className="text-xs font-bold opacity-50 mr-2">{String.fromCharCode(65 + i)}</span>
                                        {opt}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <p className="text-[11px] text-gray-400 mt-3 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {picked === null ? "Pick fast — speed = bonus points" : reveal ? "Next question coming..." : "Waiting for opponent..."}
                    </p>
                </>
            )}

            {state.winner && (
                <EndCard
                    winner={state.winner}
                    currentUserId={currentUserId}
                    myScore={myScore}
                    oppScore={oppScore}
                    oppName={opp.userName}
                    onLeave={onLeave}
                />
            )}
        </div>
    );
}

function EndCard({ winner, currentUserId, myScore, oppScore, oppName, onLeave }) {
    const youWon = winner === String(currentUserId);
    const draw = winner === "draw";
    return (
        <div className={`mt-4 px-4 py-4 rounded-lg border text-center max-w-md ${
            draw ? "bg-gray-50 border-gray-200" :
            youWon ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
        }`}>
            <Trophy className={`h-7 w-7 mx-auto mb-1 ${draw ? "text-gray-400" : youWon ? "text-emerald-600" : "text-red-500"}`} />
            <div className={`text-base font-bold ${draw ? "text-gray-700" : youWon ? "text-emerald-800" : "text-red-700"}`}>
                {draw ? `Draw! ${myScore} — ${oppScore}` : youWon ? `You won! ${myScore} — ${oppScore}` : `You lost ${myScore} — ${oppScore}`}
            </div>
            <p className="text-xs text-gray-500 mt-1">vs {oppName}</p>
            <Button size="sm" variant="outline" onClick={onLeave} className="mt-3">Back to lobby</Button>
        </div>
    );
}
